import React, { Dispatch, SetStateAction, useEffect, useState } from 'react';
import OutlineButton from 'Components/Common/components/Button/OutlineButton';
import { TChildSelected } from 'types/Pages/ProductDelta';
import ServiceWrapper from 'Services/ServiceWrapper';
import { toast } from 'Components/DesignSystem/Toast/useToast';
import AppSettings from 'Settings/AppSettings';
import AuthManager from 'Common/AuthManager';
import ModalFooter from 'Components/Common/Modal/ModalFooter';
import ModalBody from 'Components/Common/Modal/ModalBody';
import ModalHeader from 'Components/Common/Modal/ModalHeader';
import ModalContainer from 'Components/Common/Modal/ModalContainer';

interface DialogProps {
  isOpen: boolean
  onClose: () => void;
  childSelected: TChildSelected,
  setChildSelected: Dispatch<SetStateAction<TChildSelected>>,
  fetchData: () => void
}

const RemoveChildModal: React.FC<DialogProps> = ({ isOpen, onClose, childSelected, setChildSelected, fetchData }) => {
  const api_base_url = AppSettings.GMS_BACKEND_API_URL
  const [productids, setProductsIds] = useState<string[]>([])
  const [loading, setLoading] = useState<boolean>(false)

  useEffect(() => {
    setLoading(false)
    setProductsIds([])
    
    if(!isOpen) return
    setProductsIds(childSelected.map(i => i.id))
  }, [isOpen, childSelected])

  const handleSelect = (e: React.ChangeEvent<HTMLInputElement>, id: string) => {
    if(e.target.checked) 
      return setProductsIds(prev => [...prev, id])
    
    setProductsIds(prev => {
      let next = [...prev]
      next.splice(next.indexOf(id), 1)
      return next
    })
  }

  const handleSubmit = async () => {
      if(productids.length === 0 ) return toast.error("No products selected")
      setLoading(true)

      const res = await ServiceWrapper.doPut(
          api_base_url + "/product/remove_variants",
          {
              variants: productids
          },
          null,
          AuthManager.getGmsToken()
      )

      if(res.data.done) {
        setChildSelected([])
        setProductsIds([])
        onClose()
        toast.success(res.data?.message)
        fetchData()
      }
      else toast.error(res.data?.message ?? "Failed to create parent")
     
      setLoading(false)
  }

  return (
    <ModalContainer id="product-remove-child-modal" isOpen={isOpen} setOpen={onClose} width='500px'>
      <ModalHeader>
      <h2 className={`tw-text-sm tw-font-semibold tw-my-2  tw-flex tw-items-center tw-space-x-3`}>
        <span>Remove Child</span>
        <span className={`tw-top-[116px] tw-w-max tw-left-[475px] tw-rounded-[44px] tw-bg-tone-neutral-tint tw-h-[26px] tw-flex tw-flex-row tw-py-0.5 tw-px-3 tw-box-border tw-items-center tw-justify-center tw-text-ink-secondary`}>
            <span className={`tw-text-sm tw-font-normal`}>{productids.length} Selected</span>
        </span>
      </h2>
      </ModalHeader>

      <ModalBody>
      <div className={`tw-box-border tw-w-auto tw-h-px`} /> 
      <div className={`tw-my-3`}>
         <div className="tw-border tw-border-tone-warning-glyph tw-bg-tone-warning-tint tw-p-2.5 tw-flex tw-items-center tw-text-xs tw-space-x-2.5">
           <span className="w-[24px]">
             <svg xmlns="http://www.w3.org/2000/svg" width="24" height="25" viewBox="0 0 24 25" fill="none">
               <path d="M10.2898 4.35923L1.81978 18.4992C1.64514 18.8017 1.55274 19.1445 1.55177 19.4937C1.55079 19.843 1.64127 20.1864 1.8142 20.4898C1.98714 20.7931 2.2365 21.046 2.53748 21.2231C2.83847 21.4002 3.18058 21.4954 3.52978 21.4992H20.4698C20.819 21.4954 21.1611 21.4002 21.4621 21.2231C21.7631 21.046 22.0124 20.7931 22.1854 20.4898C22.3583 20.1864 22.4488 19.843 22.4478 19.4937C22.4468 19.1445 22.3544 18.8017 22.1798 18.4992L13.7098 4.35923C13.5315 4.06533 13.2805 3.82235 12.981 3.65371C12.6814 3.48508 12.3435 3.39648 11.9998 3.39648C11.656 3.39648 11.3181 3.48508 11.0186 3.65371C10.7191 3.82235 10.468 4.06533 10.2898 4.35923Z" stroke="#FFC400" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
               <path d="M12 9.5V13.5" stroke="#FFC400" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
               <path d="M12 17.5H12.01" stroke="#FFC400" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
             </svg>
           </span>
            <p>Removing child products. The system will automatically delete the parent and move the child products to individual product listings.</p>
         </div>
     </div>

     <div className="">
       <div className="tw-overflow-auto tw-border tw-max-h-[250px] tw-rounded">
         {
           childSelected.map((item, key) => {
             return(
               <div key={item.id} className={`tw-w-full tw-space-x-2 tw-p-2 tw-py-3 tw-flex tw-items-center tw-border-b tw-rounded ${productids.includes(item.id) && "tw-bg-accent-tint"}`}>
                 <span><input data-testid={`productlist-removechildmodal-input-1-${key}`} checked={productids.includes(item.id)} onChange={(e) => handleSelect(e, item.id)} type="checkbox" name={`child-modal-checkbox-${key}`} id={`child-modal-checkbox-${key}`}/></span>
                 <span><label htmlFor={`child-modal-checkbox-${key}`} className="tw-text-accent">{item.name}</label> </span>
               </div>
               )
           })
         }
       </div>
      </div>

      </ModalBody>
      <ModalFooter>
        <div className="tw-space-x-3 tw-text-right">
          <OutlineButton id="product-remove-child-modal-cancel-btn" onClick={() => onClose()}>Cancel</OutlineButton>
          <button id="product-remove-child-modal-submit-btn" data-testid="products-remove-child-submit-btn" disabled={(loading || productids.length === 0)} onClick={handleSubmit} className={`button-style`}>Remove and make it Individual</button>
        </div>
      </ModalFooter>
    </ModalContainer>
  );
};

export default RemoveChildModal;
